import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import { formatApiError } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { QrCode, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), language: lang });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 relative overflow-hidden"
      data-testid="forgot-password-page"
      style={{ marginTop: "-4rem", paddingTop: "4rem" }}
    >
      <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-fuchsia-600/10 blur-[120px] pointer-events-none" />

      <div className="grid place-items-center min-h-[calc(100vh-4rem)] p-8 relative">
        <div className="w-full max-w-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-600 grid place-items-center text-white shadow-lg shadow-purple-600/40 mb-5">
            {sent ? <MailCheck className="h-6 w-6" /> : <QrCode className="h-6 w-6" />}
          </div>

          {sent ? (
            <div className="space-y-5" data-testid="forgot-password-success">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t("auth.forgotSuccessTitle")}</h1>
                <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-sm leading-relaxed">
                  {t("auth.forgotSuccessBody")}
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 font-semibold hover:underline"
                data-testid="forgot-password-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("auth.backToLogin")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t("auth.forgotTitle")}</h1>
                <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-sm leading-relaxed">
                  {t("auth.forgotSubtitle")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-zinc-700 dark:text-zinc-300">
                  {t("auth.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email"
                  className="rounded-xl h-11"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-2xl h-11 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30"
                data-testid="forgot-password-submit"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("auth.sendResetLink")}
              </Button>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-purple-600 dark:hover:text-purple-400"
                data-testid="forgot-password-back-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("auth.backToLogin")}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
